import { Button, Toggle, useTheme } from '@sixfold/common-ui';
import { axisBottom } from 'd3-axis';
import { scaleTime, ScaleTime } from 'd3-scale';
import {
  interpolateBlues,
  interpolateGreens,
  interpolateGreys,
  interpolateOranges,
  interpolatePurples,
} from 'd3-scale-chromatic';
import { select } from 'd3-selection';
import { utcFormat } from 'd3-time-format';
import { toPng } from 'html-to-image';
import React, { useEffect, useRef, useMemo, useState, useCallback, type JSX } from 'react';

import { haversineDistanceKm } from './haversine';
import type { TimelineProps, ColorScheme, Stop, VehicleTimelineData } from './types';

import styles from './timeline.module.css';

// Tooltip content structure
interface TooltipData {
  title: string;
  subtitle?: string;
  rows: Array<{
    label: string;
    value: string;
    highlight?: boolean;
    warning?: boolean;
  }>;
}

// Tooltip state
interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  data: TooltipData | null;
}

type TooltipOptions = {
  anchor?: 'pointer' | 'element';
  offsetX?: number;
  offsetY?: number;
};

// Tooltip Component
const TimelineTooltip: React.FC<{ tooltip: TooltipState }> = ({ tooltip }) => {
  if (!tooltip.visible || !tooltip.data) return null;

  return (
    <div className={styles.tooltip} style={{ left: tooltip.x, top: tooltip.y }}>
      <div className={styles.tooltipTitle} style={{ marginBottom: tooltip.data.subtitle ? '2px' : '8px' }}>
        {tooltip.data.title}
      </div>
      {tooltip.data.subtitle && <div className={styles.tooltipSubtitle}>{tooltip.data.subtitle}</div>}
      <div className={styles.tooltipRows}>
        {tooltip.data.rows.map((row, idx) => (
          <div key={idx} className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>{row.label}</span>
            <span
              className={
                row.warning
                  ? styles.tooltipValueWarning
                  : row.highlight
                    ? styles.tooltipValueHighlight
                    : styles.tooltipValue
              }>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper types for unified event handling
interface UnifiedEvent {
  timestamp: Date;
  label: string;
  isGpsEvent?: boolean;
  isExternal: boolean;
  isArrival?: boolean;
  isDeparture?: boolean;
  tooltipContent: string;
  eventId: string;
  isSentLate?: boolean; // True if external event was sent >15 minutes after it happened
  sentDelayMinutes?: number; // The actual delay in minutes
  createdAt?: Date; // When the external event was sent
}

// Helper to format delay duration in a human-readable way
const formatDelayDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${Math.round(minutes)} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}min`;
};

const LIGHT_COLORS: ColorScheme = {
  timeslot: {
    loading: interpolateBlues(0.6),
    unloading: interpolateGreens(0.6),
    waypoint: interpolateOranges(0.6),
    other: interpolateGreys(0.6),
    completed: interpolateBlues(0.75),
    delayed: interpolateOranges(0.75),
  },
  actual: {
    arrival: '#4CAF50',
    departure: '#F44336',
  },
  background: {
    row: '#FFFFFF',
    rowAlt: '#F5F5F5',
    grid: '#E0E0E0',
    tourEventsRow: '#F0F8FF',
  },
  text: {
    primary: '#333333',
    secondary: '#666666',
    labelStroke: '#FFFFFF',
  },
};

const DARK_COLORS: ColorScheme = {
  timeslot: {
    loading: interpolateBlues(0.5),
    unloading: interpolateGreens(0.5),
    waypoint: interpolateOranges(0.5),
    other: interpolateGreys(0.4),
    completed: interpolateBlues(0.6),
    delayed: interpolateOranges(0.6),
  },
  actual: {
    arrival: '#66BB6A',
    departure: '#EF5350',
  },
  background: {
    row: '#1e1e1e',
    rowAlt: '#252525',
    grid: '#444444',
    tourEventsRow: '#1a2a3a',
  },
  text: {
    primary: '#E0E0E0',
    secondary: '#999999',
    labelStroke: '#1e1e1e',
  },
};

export const getDefaultColors = (isDark: boolean): ColorScheme => (isDark ? DARK_COLORS : LIGHT_COLORS);

const LIGHT_VEHICLE_COLORS = {
  allocation: '#4CAF50',
  allocationBorder: '#2E7D32',
  noTracking: 'rgba(128, 128, 128, 0.3)',
  nearStop: interpolatePurples(0.5),
  nearStopBorder: interpolatePurples(0.7),
  locationChunk: 'rgba(33, 150, 243, 0.4)',
  locationChunkBorder: 'rgba(33, 150, 243, 0.8)',
  break: '#FF9800',
  breakBorder: '#F57C00',
  restBreak: '#E91E63',
  restBreakBorder: '#C2185B',
  weekendBreak: '#673AB7',
  weekendBreakBorder: '#512DA8',
  ping: 'rgba(76, 175, 80, 0.5)',
  vehicleBackground: '#FAFAFA',
  vehicleBackgroundAlt: '#F0F0F0',
};

const DARK_VEHICLE_COLORS = {
  allocation: '#66BB6A',
  allocationBorder: '#43A047',
  noTracking: 'rgba(128, 128, 128, 0.25)',
  nearStop: interpolatePurples(0.4),
  nearStopBorder: interpolatePurples(0.6),
  locationChunk: 'rgba(66, 165, 245, 0.35)',
  locationChunkBorder: 'rgba(66, 165, 245, 0.7)',
  break: '#FFA726',
  breakBorder: '#FB8C00',
  restBreak: '#EC407A',
  restBreakBorder: '#D81B60',
  weekendBreak: '#7E57C2',
  weekendBreakBorder: '#5E35B1',
  ping: 'rgba(102, 187, 106, 0.45)',
  vehicleBackground: '#1a1a1a',
  vehicleBackgroundAlt: '#222222',
};

export const getVehicleColors = (isDark: boolean) => (isDark ? DARK_VEHICLE_COLORS : LIGHT_VEHICLE_COLORS);

export const useTimelineColors = () => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme?.includes('dark') ?? false;
  return useMemo(
    () => ({
      colors: getDefaultColors(isDark),
      vehicleColors: getVehicleColors(isDark),
      isDark,
    }),
    [isDark],
  );
};

// UTC formatters
const formatUtcTime = utcFormat('%H:%M');
const formatUtcTimeSeconds = utcFormat('%H:%M:%S');
const formatUtcFullDate = utcFormat('%B %d, %Y');
const formatUtcIsoDate = utcFormat('%Y-%m-%d');
const formatUtcDateTime = utcFormat('%Y-%m-%d %H:%M:%S');
const formatUtcDateShort = utcFormat('%b %d');

const buildUnifiedStopEvents = (stop: Stop): UnifiedEvent[] => {
  const unifiedStopEvents: UnifiedEvent[] = [];

  if (stop.arrival_time) {
    unifiedStopEvents.push({
      timestamp: new Date(stop.arrival_time),
      label: 'Arrival',
      isExternal: false,
      isArrival: true,
      tooltipContent: `Arrival\nTime (UTC): ${formatUtcDateTime(new Date(stop.arrival_time))}`,
      eventId: `stop-arrival-${stop.stop_id}`,
    });
  }

  if (stop.departure_time) {
    unifiedStopEvents.push({
      timestamp: new Date(stop.departure_time),
      label: 'Departure',
      isExternal: false,
      isDeparture: true,
      tooltipContent: `Departure\nTime (UTC): ${formatUtcDateTime(new Date(stop.departure_time))}`,
      eventId: `stop-departure-${stop.stop_id}`,
    });
  }

  stop.events
    .filter(
      (event) => event.event_time && !['APPROACH', 'APPROACHING', 'DEPART', 'DEPARTED'].includes(event.event_name),
    )
    .forEach((event) => {
      const isGpsEvent = event.visibilitySource?.streamPrecision === 'GPS';
      unifiedStopEvents.push({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        timestamp: new Date(event.event_time!),
        label: event.event_name,
        isGpsEvent,
        isExternal: false,
        isArrival: false,
        isDeparture: false,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tooltipContent: `${event.event_name}\nTime (UTC): ${formatUtcDateTime(new Date(event.event_time!))}${
          event.visibilitySource ? `\nSource: ${event.visibilitySource.primaryEventSource}` : ''
        }${event.visibilitySource?.streamPrecision ? `\nType: ${event.visibilitySource.streamPrecision}` : ''}`,
        eventId: `stop-event-${event.event_id}`,
      });
    });

  stop.externalEvents.forEach((event) => {
    const qualifierLower = event.qualifier.toLowerCase();
    const isArrival = qualifierLower.includes('arrival');
    const isDeparture = qualifierLower.includes('departure');
    const eventDate = new Date(event.timestamp);
    const createdDate = new Date(event.createdAt);
    const delayMinutes = (createdDate.getTime() - eventDate.getTime()) / (1000 * 60);
    const isSentLate = delayMinutes > 15;

    unifiedStopEvents.push({
      timestamp: eventDate,
      label: event.qualifier,
      isExternal: true,
      isArrival,
      isDeparture,
      tooltipContent: `${event.qualifier}\nTime (UTC): ${formatUtcDateTime(eventDate)}\nSource: ${event.eventSource}`,
      eventId: `stop-ext-event-${event.eventId}`,
      isSentLate,
      sentDelayMinutes: isSentLate ? delayMinutes : undefined,
      createdAt: createdDate,
    });
  });

  unifiedStopEvents.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  return unifiedStopEvents;
};

// Smart tick formatter: shows dates at midnight when spanning multiple days, times otherwise
const smartTickFormat = (domain: readonly [Date, Date]) => {
  const durationHours = (domain[1].getTime() - domain[0].getTime()) / (1000 * 60 * 60);
  const spansMultipleDays = durationHours > 24;

  return (tick: Date): string => {
    if (spansMultipleDays && tick.getUTCHours() === 0 && tick.getUTCMinutes() === 0) {
      return formatUtcDateShort(tick); // e.g. "Feb 08"
    }
    return formatUtcTime(tick); // e.g. "14:00"
  };
};

// Render tracking overlay (gray out non-tracking periods) - with animation support
const renderTrackingOverlay = (
  vehicle: VehicleTimelineData,
  y: number,
  height: number,
  timeScale: ScaleTime<number, number>,
  innerWidth: number,
  isExpanded: boolean,
  expandedY?: number,
  expandedHeight?: number,
  showTooltip?: (e: React.MouseEvent, data: TooltipData) => void,
  updateTooltipPosition?: (e: React.MouseEvent) => void,
  hideTooltip?: () => void,
  vColors = LIGHT_VEHICLE_COLORS,
) => {
  const domainStart = timeScale.domain()[0];
  const domainEnd = timeScale.domain()[1];

  const currentY = isExpanded && expandedY !== undefined ? expandedY : y;
  const currentHeight = isExpanded && expandedHeight !== undefined ? expandedHeight : height;
  const borderRadius = isExpanded ? 2 : 0;

  // Build tooltip data for tracking ranges
  const buildTrackingTooltip = (): TooltipData => {
    if (vehicle.trackingRanges.length === 0) {
      return {
        title: 'Tracking Range',
        subtitle: vehicle.licensePlateNumber,
        rows: [{ label: 'Status', value: 'No tracking data' }],
      };
    }

    const rows: { label: string; value: string; highlight?: boolean }[] = [];
    vehicle.trackingRanges.forEach((range, idx) => {
      const from = new Date(range.from);
      const to = new Date(range.to);
      const durationMs = to.getTime() - from.getTime();
      const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
      const durationMins = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
      rows.push({
        label: `Range ${idx + 1}`,
        value: `${formatUtcTimeSeconds(from)} - ${formatUtcTimeSeconds(to)}`,
        highlight: true,
      });
      rows.push({ label: 'Duration', value: `${durationHours}h ${durationMins}m` });
    });

    return {
      title: 'Tracking Range',
      subtitle: vehicle.licensePlateNumber,
      rows,
    };
  };

  const tooltipData = buildTrackingTooltip();
  const mouseHandlers =
    showTooltip && updateTooltipPosition && hideTooltip
      ? {
          onMouseEnter: (e: React.MouseEvent) => showTooltip(e, tooltipData),
          onMouseMove: updateTooltipPosition,
          onMouseLeave: hideTooltip,
        }
      : {};

  if (vehicle.trackingRanges.length === 0) {
    // No tracking - gray out entire row
    return (
      <rect
        x={0}
        y={currentY}
        width={innerWidth}
        height={currentHeight}
        fill={vColors.noTracking}
        stroke={isExpanded ? 'rgba(100, 100, 100, 0.5)' : 'none'}
        strokeWidth={isExpanded ? 1 : 0}
        rx={borderRadius}
        style={{
          cursor: 'default',
          transition: 'y 0.3s ease, height 0.3s ease, rx 0.3s ease',
        }}
        {...mouseHandlers}
      />
    );
  }

  // Sort tracking ranges
  const sortedRanges = [...vehicle.trackingRanges].sort(
    (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime(),
  );

  const elements: JSX.Element[] = [];

  // Add invisible rect for the entire row to capture mouse events
  elements.push(
    <rect
      key="tracking-hitarea"
      x={0}
      y={currentY}
      width={innerWidth}
      height={currentHeight}
      fill="transparent"
      style={{
        cursor: 'default',
        transition: 'y 0.3s ease, height 0.3s ease',
      }}
      {...mouseHandlers}
    />,
  );

  let lastEnd = domainStart;

  sortedRanges.forEach((range, idx) => {
    const rangeStart = new Date(range.from);
    const rangeEnd = new Date(range.to);

    // Clamp to domain
    const effectiveStart = rangeStart < domainStart ? domainStart : rangeStart;
    const effectiveEnd = rangeEnd > domainEnd ? domainEnd : rangeEnd;

    // Add gray overlay for gap before this tracking range
    if (effectiveStart > lastEnd) {
      const gapStartX = Math.max(0, timeScale(lastEnd));
      const gapEndX = Math.min(innerWidth, timeScale(effectiveStart));
      if (gapEndX > gapStartX) {
        elements.push(
          <rect
            key={`no-track-${idx}`}
            x={gapStartX}
            y={currentY}
            width={gapEndX - gapStartX}
            height={currentHeight}
            fill={vColors.noTracking}
            stroke={isExpanded ? 'rgba(100, 100, 100, 0.5)' : 'none'}
            strokeWidth={isExpanded ? 1 : 0}
            rx={borderRadius}
            pointerEvents="none"
            style={{
              transition: 'y 0.3s ease, height 0.3s ease, rx 0.3s ease',
            }}
          />,
        );
      }
    }
    lastEnd = effectiveEnd > lastEnd ? effectiveEnd : lastEnd;
  });

  // Add gray overlay for gap after last tracking range
  if (lastEnd < domainEnd) {
    const gapStartX = Math.max(0, timeScale(lastEnd));
    if (gapStartX < innerWidth) {
      elements.push(
        <rect
          key="no-track-end"
          x={gapStartX}
          y={currentY}
          width={innerWidth - gapStartX}
          height={currentHeight}
          fill={vColors.noTracking}
          stroke={isExpanded ? 'rgba(100, 100, 100, 0.5)' : 'none'}
          strokeWidth={isExpanded ? 1 : 0}
          rx={borderRadius}
          pointerEvents="none"
          style={{
            transition: 'y 0.3s ease, height 0.3s ease, rx 0.3s ease',
          }}
        />,
      );
    }
  }

  return <g>{elements}</g>;
};

// Get break colors based on type
const getBreakColors = (type: 'break' | 'rest' | 'weekendBreak', vColors = LIGHT_VEHICLE_COLORS) => {
  switch (type) {
    case 'rest':
      return { fill: vColors.restBreak, stroke: vColors.restBreakBorder };
    case 'weekendBreak':
      return { fill: vColors.weekendBreak, stroke: vColors.weekendBreakBorder };
    default:
      return { fill: vColors.break, stroke: vColors.breakBorder };
  }
};

// Render vehicle timeline with transitions (unified for both collapsed and expanded states)
const renderVehicleTimelineWithTransition = (
  vehicle: VehicleTimelineData,
  rowY: number,
  collapsedHeight: number,
  timeScale: ScaleTime<number, number>,
  innerWidth: number,
  rowId: string,
  isExpanded: boolean,
  toggleRow: (id: string) => void,
  showTooltip: (e: React.MouseEvent, data: TooltipData, options?: TooltipOptions) => void,
  updateTooltipPosition: (e: React.MouseEvent) => void,
  hideTooltip: () => void,
  themeColors: ColorScheme = LIGHT_COLORS,
  vColors = LIGHT_VEHICLE_COLORS,
) => {
  const elements: JSX.Element[] = [];
  const domainStart = timeScale.domain()[0].getTime();
  const domainEnd = timeScale.domain()[1].getTime();

  // Layout calculations for collapsed state
  // Four narrow rows: near stops (top), breaks, location, tracking (bottom)
  const collapsedRowHeight = 8; // Height of each narrow row
  const collapsedPadding = 6; // Padding from top/bottom of row
  const collapsedRowSpacing = 3; // Spacing between narrow rows

  // Collapsed positions: 4 data rows stacked vertically
  // Order: near stops (top), breaks, location, tracking (bottom)
  const collapsedNearStopY = rowY + collapsedPadding;
  const collapsedBreakY = rowY + collapsedPadding + collapsedRowHeight + collapsedRowSpacing;
  const collapsedLocationY = rowY + collapsedPadding + 2 * (collapsedRowHeight + collapsedRowSpacing);
  const collapsedCenterY = rowY + collapsedHeight / 2;

  // Expanded layout calculations
  const lineHeight = 18;
  const expandedStartY = rowY + 5;

  // Group near stops by stopId for expanded view
  const stopGroups = new Map<number, typeof vehicle.nearStopRanges>();
  vehicle.nearStopRanges.forEach((range) => {
    if (!stopGroups.has(range.stopId)) {
      stopGroups.set(range.stopId, []);
    }
    const group = stopGroups.get(range.stopId);
    if (group) group.push(range);
  });
  const uniqueStopCount = Math.max(1, stopGroups.size);

  // Line indices for expanded: 0 = allocation, 1..N = near stops, N+1 = breaks, N+2 = location, N+3 = tracking
  const allocationLineIndex = 0;
  const nearStopStartIndex = 1;
  const breakLineIndex = nearStopStartIndex + uniqueStopCount;
  const locationLineIndex = breakLineIndex + 1;
  const trackingLineIndex = locationLineIndex + 1;

  // Helper to calculate Y position for expanded view
  const getExpandedY = (lineIndex: number) => expandedStartY + lineIndex * lineHeight;

  // Helper to create clipped rect with transition and tooltip
  const createTransitionRect = (
    key: string,
    fromTime: Date,
    toTime: Date,
    collapsedY: number,
    collapsedH: number,
    expandedY: number,
    expandedH: number,
    fill: string,
    stroke: string | null,
    tooltipData: TooltipData,
  ) => {
    const fromMs = fromTime.getTime();
    const toMs = toTime.getTime();

    if (toMs < domainStart || fromMs > domainEnd) return null;

    const isClippedLeft = fromMs < domainStart;
    const isClippedRight = toMs > domainEnd;

    const effectiveFrom = isClippedLeft ? new Date(domainStart) : fromTime;
    const effectiveTo = isClippedRight ? new Date(domainEnd) : toTime;

    const startX = Math.max(0, timeScale(effectiveFrom));
    const endX = Math.min(innerWidth, timeScale(effectiveTo));
    const width = Math.max(2, endX - startX);

    const currentY = isExpanded ? expandedY : collapsedY;
    const currentH = isExpanded ? expandedH : collapsedH;
    const gradientId = `grad-${key}-${rowId}`;

    const mouseHandlers = {
      onMouseEnter: (e: React.MouseEvent) => showTooltip(e, tooltipData),
      onMouseMove: updateTooltipPosition,
      onMouseLeave: hideTooltip,
    };

    if (isClippedLeft || isClippedRight) {
      return (
        <g key={key} {...mouseHandlers}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              {isClippedLeft ? (
                <>
                  <stop offset="0%" stopColor={fill} stopOpacity="0.2" />
                  <stop offset="15%" stopColor={fill} stopOpacity="1" />
                </>
              ) : (
                <stop offset="0%" stopColor={fill} stopOpacity="1" />
              )}
              {isClippedRight ? (
                <>
                  <stop offset="85%" stopColor={fill} stopOpacity="1" />
                  <stop offset="100%" stopColor={fill} stopOpacity="0.2" />
                </>
              ) : (
                <stop offset="100%" stopColor={fill} stopOpacity="1" />
              )}
            </linearGradient>
          </defs>
          <rect
            x={startX}
            y={currentY}
            width={width}
            height={currentH}
            fill={`url(#${gradientId})`}
            stroke={stroke || undefined}
            strokeWidth={stroke ? 1 : 0}
            rx={2}
            onClick={() => toggleRow(rowId)}
            style={{
              cursor: 'pointer',
              transition: 'y 0.3s ease, height 0.3s ease',
            }}
          />
        </g>
      );
    }

    return (
      <rect
        key={key}
        x={startX}
        y={currentY}
        width={width}
        height={currentH}
        fill={fill}
        stroke={stroke || undefined}
        strokeWidth={stroke ? 1 : 0}
        rx={2}
        opacity={0.8}
        onClick={() => toggleRow(rowId)}
        style={{
          cursor: 'pointer',
          transition: 'y 0.3s ease, height 0.3s ease',
        }}
        {...mouseHandlers}
      />
    );
  };

  // 0. Individual pings - rendered as fine vertical lines at the same position as location chunks
  const expandedLocationY = getExpandedY(locationLineIndex) + 1;
  const expandedLocationHeight = 14;

  vehicle.pings.forEach((ping, idx) => {
    const pingTime = new Date(ping.timestamp);
    const pingMs = pingTime.getTime();

    if (pingMs < domainStart || pingMs > domainEnd) return;

    const x = timeScale(pingTime);
    const currentY = isExpanded ? expandedLocationY : collapsedLocationY;
    const currentHeight = isExpanded ? expandedLocationHeight : collapsedRowHeight;

    elements.push(
      <rect
        key={`ping-${idx}`}
        x={x}
        y={currentY}
        width={1}
        height={currentHeight}
        fill={vColors.ping}
        pointerEvents="none"
        style={{
          transition: 'y 0.3s ease, height 0.3s ease',
        }}
      />,
    );
  });

  // 1. Location chunks - second from bottom in collapsed, dedicated line in expanded
  vehicle.locationChunks.forEach((chunk, idx) => {
    const fromDate = new Date(chunk.from);
    const toDate = new Date(chunk.to);
    const durationMins = Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60));

    const locationTooltip: TooltipData = {
      title: 'Location Shared',
      subtitle: vehicle.licensePlateNumber,
      rows: [
        { label: 'Start (UTC)', value: formatUtcTimeSeconds(fromDate), highlight: true },
        { label: 'End (UTC)', value: formatUtcTimeSeconds(toDate), highlight: true },
        { label: 'Duration', value: `${Math.floor(durationMins / 60)}h ${durationMins % 60}m` },
        { label: 'Date (UTC)', value: formatUtcFullDate(fromDate) },
      ],
    };

    const rect = createTransitionRect(
      `loc-${idx}`,
      fromDate,
      toDate,
      collapsedLocationY,
      collapsedRowHeight,
      getExpandedY(locationLineIndex) + 1,
      14,
      vColors.locationChunk,
      vColors.locationChunkBorder,
      locationTooltip,
    );
    if (rect) elements.push(rect);
  });

  // 2. Near stop ranges - middle row in collapsed (all stops same height), separate lines in expanded
  let nearStopLineOffset = 0;
  const nearStopRightmostInfo: Record<number, { x: number; to: string; stopName: string }> = {};

  if (stopGroups.size > 0) {
    stopGroups.forEach((ranges, stopId) => {
      const expandedLineY = getExpandedY(nearStopStartIndex + nearStopLineOffset);
      nearStopRightmostInfo[stopId] = { x: -Infinity, to: '', stopName: '' };

      ranges.forEach((range, idx) => {
        const fromDate = new Date(range.from);
        const toDate = new Date(range.to);
        const durationMins = Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60));

        const nearStopTooltip: TooltipData = {
          title: 'Vehicle Near Stop',
          subtitle: range.stopName,
          rows: [
            { label: 'From (UTC)', value: formatUtcTimeSeconds(fromDate), highlight: true },
            { label: 'To (UTC)', value: formatUtcTimeSeconds(toDate), highlight: true },
            { label: 'Duration', value: `${Math.floor(durationMins / 60)}h ${durationMins % 60}m` },
            { label: 'Vehicle', value: vehicle.licensePlateNumber },
          ],
        };

        const rect = createTransitionRect(
          `near-stop-${stopId}-${idx}`,
          fromDate,
          toDate,
          collapsedNearStopY, // All near stops on same row in collapsed
          collapsedRowHeight,
          expandedLineY + 1,
          14,
          vColors.nearStop,
          vColors.nearStopBorder,
          nearStopTooltip,
        );
        if (rect) elements.push(rect);

        const endX = timeScale(new Date(range.to));
        if (endX > nearStopRightmostInfo[stopId].x && endX <= innerWidth) {
          nearStopRightmostInfo[stopId] = { x: endX, to: range.to, stopName: range.stopName };
        }
      });

      nearStopLineOffset += 1;
    });
  }

  // Near stop labels on the right side with grouping (only in expanded)
  const rightLabelX = innerWidth + 10;
  if (stopGroups.size > 0) {
    const firstNearStopY = getExpandedY(nearStopStartIndex);
    const lastNearStopY = getExpandedY(nearStopStartIndex + stopGroups.size - 1);
    const groupPadding = 4;
    const headerHeight = 12;

    // Background for near stops group
    elements.push(
      <rect
        key="near-stops-bg"
        x={rightLabelX - 4}
        y={firstNearStopY - headerHeight - groupPadding}
        width={145}
        height={lastNearStopY - firstNearStopY + 14 + headerHeight + groupPadding * 2}
        fill="rgba(156, 39, 176, 0.08)"
        rx={4}
        style={{
          opacity: isExpanded ? 1 : 0,
          transition: isExpanded ? 'opacity 0.2s ease 0.2s' : 'opacity 0.1s ease',
        }}
      />,
    );

    // Header for near stops group
    elements.push(
      <text
        key="near-stops-header"
        x={rightLabelX}
        y={firstNearStopY - groupPadding}
        fontSize="9px"
        fontWeight="600"
        fill={vColors.nearStopBorder}
        style={{
          opacity: isExpanded ? 1 : 0,
          transition: isExpanded ? 'opacity 0.2s ease 0.25s' : 'opacity 0.1s ease',
        }}>
        Near Stops
      </text>,
    );

    // Individual stop labels
    let labelOffset = 0;
    stopGroups.forEach((ranges, stopId) => {
      const expandedLineY = getExpandedY(nearStopStartIndex + labelOffset);
      const stopName = ranges[0]?.stopName || '';
      if (stopName) {
        elements.push(
          <text
            key={`near-stop-label-${stopId}`}
            x={rightLabelX}
            y={expandedLineY + 11}
            fontSize="9px"
            fill={themeColors.text.secondary}
            style={{
              opacity: isExpanded ? 1 : 0,
              transition: isExpanded ? 'opacity 0.2s ease 0.25s' : 'opacity 0.1s ease',
            }}>
            {stopName.length > 18 ? `${stopName.substring(0, 18)}...` : stopName}
          </text>,
        );
      }
      labelOffset += 1;
    });
  }

  // 3. Breaks - second row in collapsed, dedicated line in expanded
  vehicle.breaks.forEach((breakItem, idx) => {
    const breakStyle = getBreakColors(breakItem.type, vColors);
    const breakStart = new Date(breakItem.from);
    const breakEnd = breakItem.to ? new Date(breakItem.to) : new Date(breakStart.getTime() + 30 * 60 * 1000);
    const durationMins = Math.round((breakEnd.getTime() - breakStart.getTime()) / (1000 * 60));
    const breakTypeName =
      breakItem.type === 'weekendBreak' ? 'Weekend Break' : breakItem.type === 'rest' ? 'Rest Break' : 'Break';

    const breakTooltip: TooltipData = {
      title: breakTypeName,
      subtitle: vehicle.licensePlateNumber,
      rows: [
        { label: 'Start (UTC)', value: formatUtcTimeSeconds(breakStart), highlight: true },
        { label: 'End (UTC)', value: breakItem.to ? formatUtcTimeSeconds(breakEnd) : 'Ongoing', highlight: true },
        { label: 'Duration', value: `${Math.floor(durationMins / 60)}h ${durationMins % 60}m` },
        { label: 'Date (UTC)', value: formatUtcFullDate(breakStart) },
      ],
    };

    const rect = createTransitionRect(
      `break-${idx}`,
      breakStart,
      breakEnd,
      collapsedBreakY,
      collapsedRowHeight,
      getExpandedY(breakLineIndex) + 1,
      14,
      breakStyle.fill,
      breakStyle.stroke,
      breakTooltip,
    );
    if (rect) elements.push(rect);
  });

  // Break label on the right side (only in expanded)
  if (vehicle.breaks.length > 0) {
    elements.push(
      <text
        key="break-label"
        x={rightLabelX}
        y={getExpandedY(breakLineIndex) + 11}
        fontSize="9px"
        fill={themeColors.text.secondary}
        style={{
          opacity: isExpanded ? 1 : 0,
          transition: isExpanded ? 'opacity 0.2s ease 0.25s' : 'opacity 0.1s ease',
        }}>
        Breaks
      </text>,
    );
  }

  // Location label on the right side (only in expanded)
  if (vehicle.locationChunks.length > 0 || vehicle.pings.length > 0) {
    elements.push(
      <text
        key="location-label"
        x={rightLabelX}
        y={getExpandedY(locationLineIndex) + 11}
        fontSize="9px"
        fill={themeColors.text.secondary}
        style={{
          opacity: isExpanded ? 1 : 0,
          transition: isExpanded ? 'opacity 0.2s ease 0.25s' : 'opacity 0.1s ease',
        }}>
        Location shared
      </text>,
    );
  }

  // 4. Allocation event - vertical line in collapsed, diamond in expanded
  if (vehicle.allocatedAt) {
    const allocMs = new Date(vehicle.allocatedAt).getTime();
    if (allocMs >= domainStart && allocMs <= domainEnd) {
      const allocDate = new Date(vehicle.allocatedAt);
      const x = timeScale(allocDate);

      // Collapsed: vertical line spanning full row
      const collapsedWidth = 3;
      const collapsedLineHeight = collapsedHeight - 4; // Slight padding
      const collapsedLineY = rowY + 2;

      // Expanded: diamond shape at top line
      const expandedSize = 10;
      const expandedY = getExpandedY(allocationLineIndex) + 8;

      // Current dimensions based on state
      const currentWidth = isExpanded ? expandedSize : collapsedWidth;
      const currentHeight = isExpanded ? expandedSize : collapsedLineHeight;
      const currentY = isExpanded ? expandedY - expandedSize / 2 : collapsedLineY;
      const currentX = x - currentWidth / 2;
      const rotation = isExpanded ? 45 : 0;

      const allocationTooltip: TooltipData = {
        title: 'Vehicle Allocated',
        subtitle: vehicle.licensePlateNumber,
        rows: [
          { label: 'Time (UTC)', value: formatUtcTimeSeconds(allocDate), highlight: true },
          { label: 'Date (UTC)', value: formatUtcFullDate(allocDate) },
        ],
      };

      elements.push(
        <g key="allocation" onClick={() => toggleRow(rowId)} style={{ cursor: 'pointer' }}>
          <rect
            x={currentX}
            y={currentY}
            width={currentWidth}
            height={currentHeight}
            fill={vColors.allocation}
            stroke={vColors.allocationBorder}
            strokeWidth={1.5}
            rx={isExpanded ? 1 : 1}
            style={{
              transformOrigin: `${x}px ${isExpanded ? expandedY : collapsedCenterY}px`,
              transform: `rotate(${rotation}deg)`,
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => showTooltip(e, allocationTooltip)}
            onMouseMove={updateTooltipPosition}
            onMouseLeave={hideTooltip}
          />
          <text
            x={x + (isExpanded ? 12 : 6)}
            y={isExpanded ? expandedY + 3 : collapsedCenterY + 3}
            fontSize="9px"
            fill={themeColors.text.primary}
            style={{
              opacity: isExpanded ? 1 : 0,
              transition: isExpanded ? 'opacity 0.2s ease 0.25s, x 0.3s ease, y 0.3s ease' : 'opacity 0.1s ease',
              pointerEvents: 'none',
            }}>
            Allocated at {formatUtcTimeSeconds(allocDate)} UTC
          </text>
        </g>,
      );
    }
  }

  // 5. Tracking label on the right side (only in expanded)
  if (vehicle.trackingRanges.length > 0) {
    elements.push(
      <text
        key="tracking-label"
        x={rightLabelX}
        y={getExpandedY(trackingLineIndex) + 11}
        fontSize="9px"
        fill={themeColors.text.secondary}
        style={{
          opacity: isExpanded ? 1 : 0,
          transition: isExpanded ? 'opacity 0.2s ease 0.25s' : 'opacity 0.1s ease',
        }}>
        Tracking range
      </text>,
    );
  }

  return <g>{elements}</g>;
};

export const Timeline: React.FC<TimelineProps> = ({
  data,
  width = 1200,
  rowHeight = 60,
  margin = { top: 20, right: 160, bottom: 50, left: 250 },
  timeScale: externalTimeScale,
}) => {
  const { colors, vehicleColors } = useTimelineColors();
  const exportRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const axisRef = useRef<SVGGElement>(null);
  const [isExportingPng, setIsExportingPng] = useState(false);
  const [platformOrderEnabled, setPlatformOrderEnabled] = useState(false);
  const orderedStops = useMemo(() => {
    if (!platformOrderEnabled) {
      return data.stops;
    }

    return [...data.stops]
      .map((stop, index) => ({ stop, index }))
      .sort((a, b) => {
        const sequenceDiff =
          (a.stop.platform_sequence_number ?? Number.MAX_SAFE_INTEGER) -
          (b.stop.platform_sequence_number ?? Number.MAX_SAFE_INTEGER);
        if (sequenceDiff !== 0) {
          return sequenceDiff;
        }

        return a.index - b.index;
      })
      .map(({ stop }) => stop);
  }, [data.stops, platformOrderEnabled]);

  // State for expanded events - vehicles start expanded by default
  const [expandedRows, setExpandedRows] = useState<Set<string>>(() => {
    const initialExpanded = new Set<string>();
    data.vehicles.forEach((v) => initialExpanded.add(`vehicle-${v.vehicleId}`));
    return initialExpanded;
  });

  // Tooltip state
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    data: null,
  });

  // Tooltip handlers
  const showTooltip = useCallback<(e: React.MouseEvent, data: TooltipData, options?: TooltipOptions) => void>(
    (e, data, options) => {
      const { anchor = 'pointer', offsetX = 0, offsetY = 0 } = options || {};
      let x = e.clientX;
      let y = e.clientY;

      if (anchor === 'element' && e.currentTarget) {
        const rect = (e.currentTarget as Element).getBoundingClientRect();
        x = rect.left + rect.width / 2;
        y = rect.top + rect.height;
      }

      setTooltip({
        visible: true,
        x: x + offsetX,
        y: y + offsetY,
        data,
      });
    },
    [],
  );

  const updateTooltipPosition = useCallback((e: React.MouseEvent) => {
    setTooltip((prev) => ({
      ...prev,
      x: e.clientX,
      y: e.clientY,
    }));
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  const toggleRowExpanded = (rowId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  const handleExportPng = useCallback(async () => {
    if (isExportingPng) {
      return;
    }

    const exportElement = exportRef.current;
    if (!exportElement) {
      return;
    }

    setIsExportingPng(true);
    let exportCloneWrapper: HTMLDivElement | null = null;

    try {
      const exportClone = exportElement.cloneNode(true) as HTMLDivElement;
      // Omit interactive header controls from the static PNG (checkbox, Save PNG button).
      exportClone.querySelector(`.${styles.controlsRow}`)?.remove();
      exportClone.querySelectorAll('text').forEach((textNode) => {
        textNode.setAttribute('dominant-baseline', 'middle');
        textNode.setAttribute('alignment-baseline', 'middle');
      });

      exportCloneWrapper = document.createElement('div');
      exportCloneWrapper.style.position = 'fixed';
      exportCloneWrapper.style.left = '-10000px';
      exportCloneWrapper.style.top = '0';
      exportCloneWrapper.style.pointerEvents = 'none';
      exportCloneWrapper.style.background = '#ffffff';
      exportCloneWrapper.appendChild(exportClone);
      document.body.appendChild(exportCloneWrapper);

      const pngUrl = await toPng(exportClone, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: Math.max(2, window.devicePixelRatio || 1),
        style: {
          backgroundColor: '#ffffff',
        },
      });
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `tour-${data.tour_id}-events.png`;
      downloadLink.click();
    } catch (error) {
      console.error('Failed to export timeline PNG', error);
    } finally {
      if (exportCloneWrapper) {
        exportCloneWrapper.remove();
      }
      setIsExportingPng(false);
    }
  }, [data.tour_id, isExportingPng]);

  // Calculate dimensions with dynamic height based on expanded rows
  const getRowHeight = (rowId: string, eventCount: number) => {
    // Tour events row is narrower
    if (rowId === 'tour-events') {
      const tourRowHeight = 40;
      if (expandedRows.has(rowId) && eventCount > 0) {
        // First event stays at center (tourRowHeight/2), others stack below with 16px spacing
        // Add bottom margin similar to the top (tourRowHeight/2 covers the top margin)
        return Math.max(tourRowHeight, tourRowHeight / 2 + eventCount * 16);
      }
      return tourRowHeight;
    }

    if (expandedRows.has(rowId) && eventCount > 0) {
      // When expanded: base row height + space for events (16px each) + margin below
      return rowHeight + eventCount * 16 + 12;
    }
    // When collapsed: events are centered on the bar, so no extra height needed
    return rowHeight;
  };

  // Calculate row heights for vehicle rows
  const getVehicleRowHeight = (rowId: string, isExpanded: boolean) => {
    if (!isExpanded) {
      // Collapsed: 3 narrow rows (location, near stops, breaks) + padding
      // Each row is 10px + 2px spacing between them + 7px padding top/bottom = 48px
      return 48; // Slightly taller to accommodate the 3 stacked data rows
    }
    // When expanded: allocation (0) + location (1) + unique near stops (2..N+1) + breaks (N+2) + tracking (N+3)
    const vehicle = data.vehicles.find((v) => rowId === `vehicle-${v.vehicleId}`);
    // Count unique stops, not total ranges
    const uniqueStopIds = new Set(vehicle?.nearStopRanges.map((r) => r.stopId) || []);
    const uniqueStopCount = uniqueStopIds.size;
    // subRows = allocation + location + near stops (at least 1) + breaks + tracking = 4 + max(1, uniqueStopCount)
    const subRows = 4 + Math.max(1, uniqueStopCount);
    return 10 + subRows * 18; // Minimal padding + lines
  };

  const rowHeights = useMemo(() => {
    const heights: Record<string, number> = {};

    // Tour events row
    const tourEventCount = data.tourEvents.filter((e) => e.event_time).length + data.tourExternalEvents.length;
    heights['tour-events'] = getRowHeight('tour-events', tourEventCount);

    // Stop rows
    orderedStops.forEach((stop) => {
      const stopEventCount =
        (stop.arrival_time ? 1 : 0) +
        (stop.departure_time ? 1 : 0) +
        stop.events.filter(
          (e) => e.event_time && !['APPROACH', 'APPROACHING', 'DEPART', 'DEPARTED'].includes(e.event_name),
        ).length +
        stop.externalEvents.length;
      heights[`stop-${stop.stop_id}`] = getRowHeight(`stop-${stop.stop_id}`, stopEventCount);
    });

    // Vehicle rows
    data.vehicles.forEach((vehicle) => {
      const rowId = `vehicle-${vehicle.vehicleId}`;
      const isExpanded = expandedRows.has(rowId);
      heights[rowId] = getVehicleRowHeight(rowId, isExpanded);
    });

    return heights;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, expandedRows, orderedStops]);

  // Calculate stops panel height
  const stopsHeight =
    rowHeights['tour-events'] + orderedStops.reduce((sum, stop) => sum + rowHeights[`stop-${stop.stop_id}`], 0);

  // Add spacing between stops and vehicles panel (includes room for stops x-axis and date labels)
  const panelSpacing = data.vehicles.length > 0 ? 80 : 0;

  // Spacing between vehicle rows
  const vehicleRowSpacing = 8;

  // Calculate vehicles panel height (including spacing between rows)
  const vehiclesHeight = data.vehicles.reduce(
    (sum, vehicle, idx) =>
      sum + rowHeights[`vehicle-${vehicle.vehicleId}`] + (idx < data.vehicles.length - 1 ? vehicleRowSpacing : 0),
    0,
  );

  const totalHeight = stopsHeight + panelSpacing + vehiclesHeight;
  const height = totalHeight + margin.top + margin.bottom + (data.vehicles.length > 0 ? 50 : 0); // Extra 50 for vehicle x-axis
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Calculate time domain based ONLY on stops and tour events (not vehicle data)
  // Vehicle elements that extend beyond will be clipped
  const timeDomain = useMemo(() => {
    const allTimes = data.stops.flatMap((stop) => {
      const times: Date[] = [];

      // Include timeslot if available
      if (stop.timeslot) {
        times.push(new Date(stop.timeslot.begin));
        times.push(new Date(stop.timeslot.end));
      }

      // Include actual arrival time if available
      if (stop.arrival_time) {
        times.push(new Date(stop.arrival_time));
      }

      // Include actual departure time if available
      if (stop.departure_time) {
        times.push(new Date(stop.departure_time));
      }

      // Include stop events
      stop.events.forEach((event) => {
        if (event.event_time) {
          times.push(new Date(event.event_time));
        }
      });

      // Include external stop events
      stop.externalEvents.forEach((event) => {
        times.push(new Date(event.timestamp));
      });

      return times;
    });

    // Include tour events
    data.tourEvents.forEach((event) => {
      if (event.event_time) {
        allTimes.push(new Date(event.event_time));
      }
    });

    // Include external tour events
    data.tourExternalEvents.forEach((event) => {
      allTimes.push(new Date(event.timestamp));
    });

    // NOTE: Vehicle data times are NOT included - vehicle elements will be clipped if they extend beyond

    // Guard against empty times array - return a sensible default domain
    if (allTimes.length === 0) {
      const now = new Date();
      const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return [dayAgo, now];
    }

    return [
      new Date(Math.min(...allTimes.map((d) => d.getTime()))),
      new Date(Math.max(...allTimes.map((d) => d.getTime()))),
    ];
  }, [data.stops, data.tourEvents, data.tourExternalEvents]);

  // Create time scale - use external scale if provided (for zoom support)
  const internalTimeScale = useMemo(
    () => scaleTime().domain(timeDomain).range([0, innerWidth]).nice(),
    [timeDomain, innerWidth],
  );

  const timeScale = externalTimeScale || internalTimeScale;

  // Get the actual visible domain from the timeScale (accounts for zoom)
  const visibleDomain = useMemo(() => {
    const domain = timeScale.domain();
    return [domain[0], domain[1]] as [Date, Date];
  }, [timeScale]);

  const stopRowStarts = useMemo(() => {
    const positions: Record<number, number> = {};
    let currentY = rowHeights['tour-events'];

    orderedStops.forEach((stop) => {
      positions[stop.stop_id] = currentY;
      currentY += rowHeights[`stop-${stop.stop_id}`];
    });

    return positions;
  }, [orderedStops, rowHeights]);

  const actualStopEventDots = useMemo(() => {
    const [domainStart, domainEnd] = timeScale.domain();

    return orderedStops.flatMap((stop, stopIndex) => {
      const rowStartY = stopRowStarts[stop.stop_id];
      if (rowStartY === undefined) {
        return [];
      }

      const isExpanded = expandedRows.has(`stop-${stop.stop_id}`);
      const barHeight = rowHeight * 0.45;
      const barY = rowStartY + (rowHeight - barHeight) / 2;
      const centerOfTimeslot = barY + barHeight / 2;
      const eventsLineY = barY + barHeight + 12;

      return buildUnifiedStopEvents(stop)
        .filter((event) => {
          const eventTime = event.timestamp.getTime();
          return eventTime >= domainStart.getTime() && eventTime <= domainEnd.getTime();
        })
        .flatMap((event, eventIndex) => {
          const isActualArrivalOrDeparture =
            !event.isExternal && (event.label === 'Arrival' || event.label === 'Departure');

          if (!isActualArrivalOrDeparture) {
            return [];
          }

          const collapsedY = centerOfTimeslot;
          const expandedY = eventsLineY + eventIndex * 16;

          return [
            {
              eventId: event.eventId,
              stopId: stop.stop_id,
              stopIndex,
              timestamp: event.timestamp,
              x: timeScale(event.timestamp),
              y: isExpanded ? expandedY : collapsedY,
              type: event.isArrival ? ('arrival' as const) : ('departure' as const),
              isArrival: event.isArrival,
              isDeparture: event.isDeparture,
            },
          ];
        });
    });
  }, [expandedRows, orderedStops, rowHeight, stopRowStarts, timeScale]);

  const sameTimestampTypeSegments = useMemo(() => {
    const segments: Array<{
      key: string;
      lineA: { x1: number; y1: number; x2: number; y2: number };
      lineB: { x1: number; y1: number; x2: number; y2: number };
    }> = [];

    const groups = new Map<string, typeof actualStopEventDots>();

    actualStopEventDots.forEach((event) => {
      const groupKey = `${event.type}-${event.timestamp.getTime()}`;
      const currentGroup = groups.get(groupKey) ?? [];
      currentGroup.push(event);
      groups.set(groupKey, currentGroup);
    });

    groups.forEach((group) => {
      if (group.length < 2) {
        return;
      }

      const sortedGroup = [...group].sort((a, b) => a.stopIndex - b.stopIndex);

      for (let index = 0; index < sortedGroup.length - 1; index++) {
        const from = sortedGroup[index];
        const to = sortedGroup[index + 1];
        const dx = to.x - from.x;
        const dy = to.y - from.y;
        const distance = Math.hypot(dx, dy);

        if (distance === 0) {
          continue;
        }

        const offset = 2;
        const normalX = -dy / distance;
        const normalY = dx / distance;

        segments.push({
          key: `${from.eventId}-${to.eventId}`,
          lineA: {
            x1: from.x + normalX * offset,
            y1: from.y + normalY * offset,
            x2: to.x + normalX * offset,
            y2: to.y + normalY * offset,
          },
          lineB: {
            x1: from.x - normalX * offset,
            y1: from.y - normalY * offset,
            x2: to.x - normalX * offset,
            y2: to.y - normalY * offset,
          },
        });
      }
    });

    return segments;
  }, [actualStopEventDots]);

  const stopConnectionSegments = useMemo(() => {
    const chronologicallySortedStopEventDots = [...actualStopEventDots].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
    );

    return chronologicallySortedStopEventDots.flatMap((event, index) => {
      const nextEvent = chronologicallySortedStopEventDots[index + 1];
      if (!event.isDeparture || !nextEvent?.isArrival) {
        return [];
      }

      const dx = nextEvent.x - event.x;
      const dy = nextEvent.y - event.y;
      const distance = Math.hypot(dx, dy);
      if (distance === 0) {
        return [];
      }

      const trim = Math.min(4.5, distance / 2);

      return [
        {
          key: `${event.eventId}-${nextEvent.eventId}`,
          distanceKm: (() => {
            const fromStop = orderedStops.find((stop) => stop.stop_id === event.stopId);
            const toStop = orderedStops.find((stop) => stop.stop_id === nextEvent.stopId);
            const fromPosition = fromStop?.location.position;
            const toPosition = toStop?.location.position;

            if (
              !fromPosition ||
              !toPosition ||
              typeof fromPosition.lat !== 'number' ||
              typeof fromPosition.lng !== 'number' ||
              typeof toPosition.lat !== 'number' ||
              typeof toPosition.lng !== 'number'
            ) {
              return null;
            }

            return haversineDistanceKm(fromPosition.lat, fromPosition.lng, toPosition.lat, toPosition.lng);
          })(),
          x1: event.x + (dx / distance) * trim,
          y1: event.y + (dy / distance) * trim,
          x2: nextEvent.x - (dx / distance) * trim,
          y2: nextEvent.y - (dy / distance) * trim,
        },
      ];
    });
  }, [actualStopEventDots, orderedStops]);

  // Compute all midnights within the visible domain (UTC time)
  const midnightDates = useMemo(() => {
    const [start, end] = visibleDomain;
    if (!start || !end) return [] as Date[];

    // Use UTC methods to find midnight in UTC
    const firstMidnight = new Date(
      Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 0, 0, 0, 0),
    );
    if (firstMidnight < start) {
      firstMidnight.setUTCDate(firstMidnight.getUTCDate() + 1);
    }

    const dates: Date[] = [];
    const cursor = new Date(firstMidnight);
    while (cursor <= end) {
      dates.push(new Date(cursor));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return dates;
  }, [visibleDomain]);

  // Compute direct distances (km) between consecutive stops using Haversine
  const consecutiveDistancesKm = useMemo(() => {
    const distances: Array<number | null> = [];
    for (let i = 0; i < orderedStops.length - 1; i++) {
      const a = orderedStops[i]?.location?.position;
      const b = orderedStops[i + 1]?.location?.position;
      if (a && b) {
        distances.push(haversineDistanceKm(a.lat, a.lng, b.lat, b.lng));
      } else {
        distances.push(null);
      }
    }
    return distances;
  }, [orderedStops]);

  // Compute tick values at nice UTC intervals, adapting to zoom level
  const axisTicks = useMemo(() => {
    const [start, end] = visibleDomain;
    if (!start || !end) return [];

    const durationMs = end.getTime() - start.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);

    // Choose an interval based on visible duration to get ~8-12 ticks
    // Intervals must divide 24 evenly for nice alignment: 1, 2, 3, 4, 6, 8, 12, 24
    let intervalHours: number;
    if (durationHours <= 6) {
      intervalHours = 1; // ~6 ticks for 6 hours
    } else if (durationHours <= 12) {
      intervalHours = 2; // ~6 ticks for 12 hours
    } else if (durationHours <= 24) {
      intervalHours = 3; // ~8 ticks for 24 hours
    } else if (durationHours <= 48) {
      intervalHours = 6; // ~8 ticks for 48 hours
    } else if (durationHours <= 96) {
      intervalHours = 12; // ~8 ticks for 4 days
    } else {
      intervalHours = 24; // 1 tick per day for longer periods
    }

    // Find first tick at interval boundary
    const startHour = start.getUTCHours();
    const firstTickHour = Math.ceil(startHour / intervalHours) * intervalHours;
    const firstTick = new Date(
      Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), firstTickHour, 0, 0, 0),
    );
    if (firstTick < start) {
      firstTick.setUTCHours(firstTick.getUTCHours() + intervalHours);
    }

    const ticks: Date[] = [];
    const cursor = new Date(firstTick);
    while (cursor <= end) {
      ticks.push(new Date(cursor));
      cursor.setUTCHours(cursor.getUTCHours() + intervalHours);
    }
    return ticks;
  }, [visibleDomain]);

  // Render axis
  useEffect(() => {
    if (!axisRef.current) return;

    const tickFormat = smartTickFormat(visibleDomain);
    const axis = axisBottom(timeScale)
      .tickValues(axisTicks)
      .tickFormat((d) => tickFormat(d as Date));

    select<SVGGElement, unknown>(axisRef.current).call(axis);
  }, [timeScale, axisTicks, visibleDomain]);

  return (
    <div className={styles.container}>
      <div ref={exportRef}>
        <h2 className={styles.header} style={{ color: colors.text.primary }}>
          Tour #{data.tour_id} (UTC)
        </h2>
        <div className={styles.headerMetaRow}>
          <p className={styles.subtitle} style={{ color: colors.text.secondary }}>
            All timestamps displayed in UTC
          </p>
          <div className={styles.controlsRow}>
            <Toggle
              label="Platform order"
              checked={platformOrderEnabled}
              onChange={({ checked }) => setPlatformOrderEnabled(checked)}
            />
            <Button size="small" kind="subtle" onClick={() => void handleExportPng()} disabled={isExportingPng}>
              {isExportingPng ? 'Saving PNG...' : 'Save PNG'}
            </Button>
          </div>
        </div>
        <svg
          ref={svgRef}
          width={width}
          height={height}
          style={{ border: '1px solid var(--color-neutral-border-subtle)', overflow: 'hidden' }}>
          <defs>
            {/* Clip path for the main timeline content area */}
            <clipPath id="timeline-clip">
              <rect x={0} y={0} width={innerWidth} height={innerHeight} />
            </clipPath>
            {/* Clip path for stops section - clips content to the chart area */}
            <clipPath id="stops-clip">
              <rect x={0} y={0} width={innerWidth} height={stopsHeight + 50} />
            </clipPath>
            {/* Clip path for vehicle section */}
            <clipPath id="vehicle-clip">
              <rect x={0} y={0} width={innerWidth} height={vehiclesHeight + 50} />
            </clipPath>
          </defs>
          <g transform={`translate(${margin.left},${margin.top})`}>
            {/* Grid lines - using UTC-aligned ticks, clipped to timeline area */}
            <g clipPath="url(#timeline-clip)">
              {axisTicks.map((tick, i) => (
                <line
                  key={i}
                  x1={timeScale(tick)}
                  x2={timeScale(tick)}
                  y1={0}
                  y2={innerHeight}
                  stroke={colors.background.grid}
                  strokeWidth={1}
                  strokeDasharray="2,2"
                />
              ))}
            </g>

            {/* Tour events row */}
            <g key="tour-events-row">
              {(() => {
                const currentRowHeight = rowHeights['tour-events'];

                return (
                  <>
                    {/* Row background - main timeline area */}
                    <rect
                      x={0}
                      y={0}
                      width={innerWidth}
                      height={currentRowHeight}
                      fill={colors.background.tourEventsRow}
                      onClick={() => toggleRowExpanded('tour-events')}
                      style={{ cursor: 'pointer' }}
                    />

                    {/* Row separator */}
                    <line
                      x1={-margin.left}
                      x2={innerWidth}
                      y1={currentRowHeight}
                      y2={currentRowHeight}
                      stroke={colors.background.grid}
                      strokeWidth={2}
                    />
                  </>
                );
              })()}

              {/* Tour label - show "Tour Events" label similar to stop labels */}
              {(data.tourEvents.length > 0 || data.tourExternalEvents.length > 0) && (
                <foreignObject
                  x={-margin.left + 10}
                  y={5}
                  width={margin.left - 20}
                  height={Math.min(35, rowHeights['tour-events'] - 10)}>
                  <div className={styles.rowLabel}>
                    <div className={styles.rowLabelTitle} style={{ color: colors.text.primary }}>
                      Tour Events
                    </div>
                  </div>
                </foreignObject>
              )}

              {/* Tour events */}
              {(() => {
                const isExpanded = expandedRows.has('tour-events');

                // Unify all tour events
                const unifiedTourEvents: UnifiedEvent[] = [
                  ...data.tourEvents
                    .filter((event) => event.event_time)
                    .map((event): UnifiedEvent => {
                      const isGpsEvent = event.visibilitySource?.streamPrecision === 'GPS';
                      const eventNameUpper = event.event_name.toUpperCase();
                      const isStart = eventNameUpper.includes('START');
                      const isComplete = eventNameUpper.includes('COMPLETE');
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      const eventDate = new Date(event.event_time!);
                      return {
                        timestamp: eventDate,
                        label: event.event_name,
                        isGpsEvent,
                        isExternal: false,
                        isArrival: isStart,
                        isDeparture: isComplete,
                        tooltipContent: `${event.event_name}\nTime (UTC): ${formatUtcDateTime(eventDate)}${
                          event.visibilitySource ? `\nSource: ${event.visibilitySource.primaryEventSource}` : ''
                        }${event.visibilitySource?.streamPrecision ? `\nType: ${event.visibilitySource.streamPrecision}` : ''}`,
                        eventId: `tour-event-${event.event_id}`,
                      };
                    }),
                  ...data.tourExternalEvents.map((event): UnifiedEvent => {
                    const qualifierLower = event.qualifier.toLowerCase();
                    const isArrival = qualifierLower.includes('arrival');
                    const isDeparture = qualifierLower.includes('departure');
                    const eventDate = new Date(event.timestamp);
                    const createdDate = new Date(event.createdAt);
                    const delayMinutes = (createdDate.getTime() - eventDate.getTime()) / (1000 * 60);
                    const isSentLate = delayMinutes > 15;
                    return {
                      timestamp: eventDate,
                      label: event.qualifier,
                      isExternal: true,
                      isArrival,
                      isDeparture,
                      tooltipContent: `${event.qualifier}\nTime (UTC): ${formatUtcDateTime(eventDate)}\nSource: ${event.eventSource}`,
                      eventId: `tour-ext-event-${event.eventId}`,
                      isSentLate,
                      sentDelayMinutes: isSentLate ? delayMinutes : undefined,
                      createdAt: createdDate,
                    };
                  }),
                ];

                // Sort by timestamp
                unifiedTourEvents.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

                const tourRowHeight = 40;
                const eventsLineY = tourRowHeight / 2; // Center of the narrower tour row

                // Helper to create tooltip data for tour events
                const createTourEventTooltip = (event: UnifiedEvent): TooltipData => ({
                  title: event.label,
                  subtitle: event.isExternal ? 'External Event' : 'Tour Event',
                  rows: [
                    { label: 'Time (UTC)', value: formatUtcTimeSeconds(event.timestamp), highlight: true },
                    { label: 'Date (UTC)', value: formatUtcFullDate(event.timestamp) },
                    ...(event.createdAt ? [{ label: 'Sent (UTC)', value: formatUtcDateTime(event.createdAt) }] : []),
                    ...(event.isSentLate && event.sentDelayMinutes !== undefined
                      ? [
                          {
                            label: 'Warning',
                            value: `Sent ${formatDelayDuration(event.sentDelayMinutes)} late`,
                            warning: true,
                          },
                        ]
                      : []),
                    ...(event.isGpsEvent !== undefined
                      ? [{ label: 'Type', value: event.isGpsEvent ? 'GPS' : 'Non-GPS' }]
                      : []),
                  ],
                });

                // Get the current time domain for filtering out-of-range events
                const [domainStart, domainEnd] = timeScale.domain();

                // Filter events that are within the visible time domain
                const visibleTourEvents = unifiedTourEvents.filter((event) => {
                  const eventTime = event.timestamp.getTime();
                  return eventTime >= domainStart.getTime() && eventTime <= domainEnd.getTime();
                });

                return (
                  <>
                    {/* Render events */}
                    {visibleTourEvents.map((event, index) => {
                      const x = timeScale(event.timestamp);
                      const collapsedY = eventsLineY;
                      const expandedY = eventsLineY + index * 16; // Keep first event in place, stack rest below
                      const y = isExpanded ? expandedY : collapsedY;
                      const eventTooltip = createTourEventTooltip(event);

                      return (
                        <g
                          key={event.eventId}
                          onClick={() => toggleRowExpanded('tour-events')}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => showTooltip(e, eventTooltip)}
                          onMouseMove={updateTooltipPosition}
                          onMouseLeave={hideTooltip}>
                          <g
                            style={{ transition: 'transform 0.3s ease', transform: `translateY(${y - collapsedY}px)` }}>
                            {event.isExternal ? (
                              <rect
                                x={x - 3.5}
                                y={collapsedY - 3.5}
                                width={7}
                                height={7}
                                fill={event.isArrival ? '#4CAF50' : event.isDeparture ? '#F44336' : '#BDBDBD'}
                                stroke={event.isArrival ? '#2E7D32' : event.isDeparture ? '#C62828' : '#9E9E9E'}
                                strokeWidth={1.5}
                                opacity={0.9}
                              />
                            ) : (
                              <circle
                                cx={x}
                                cy={collapsedY}
                                r={3.5}
                                style={{
                                  fill: event.isArrival ? '#4CAF50' : event.isDeparture ? '#F44336' : '#BDBDBD',
                                  stroke: event.isArrival ? '#2E7D32' : event.isDeparture ? '#C62828' : '#9E9E9E',
                                  strokeWidth: 1.5,
                                  opacity: 0.9,
                                }}
                              />
                            )}
                            <text
                              x={x + 6}
                              y={collapsedY}
                              fontSize="10px"
                              fill={event.isSentLate ? '#C62828' : colors.text.primary}
                              dominantBaseline="middle"
                              style={{
                                opacity: isExpanded ? 1 : 0,
                                transition: isExpanded ? 'opacity 0.15s ease 0.2s' : 'opacity 0s',
                              }}>
                              {event.label} - {formatUtcTimeSeconds(event.timestamp)} UTC
                            </text>
                          </g>
                        </g>
                      );
                    })}
                  </>
                );
              })()}
            </g>

            {/* Rows */}
            {orderedStops.map((stop, index) => {
              // Calculate cumulative Y position
              let y = rowHeights['tour-events'];
              for (let i = 0; i < index; i++) {
                y += rowHeights[`stop-${orderedStops[i].stop_id}`];
              }
              const currentRowHeight = rowHeights[`stop-${stop.stop_id}`];
              const isExpanded = expandedRows.has(`stop-${stop.stop_id}`);

              // Calculate bar dimensions (used for both timeslot rendering and event positioning)
              const barHeight = rowHeight * 0.45; // 25% narrower than before (was 0.6)
              const barY = y + (rowHeight - barHeight) / 2;

              return (
                <g key={stop.stop_id}>
                  {/* Row background - main timeline area */}
                  <rect
                    x={0}
                    y={y}
                    width={innerWidth}
                    height={currentRowHeight}
                    fill={index % 2 === 0 ? colors.background.row : colors.background.rowAlt}
                    onClick={() => toggleRowExpanded(`stop-${stop.stop_id}`)}
                    style={{ cursor: 'pointer' }}
                  />

                  {/* Row separator */}
                  <line
                    x1={-margin.left}
                    x2={innerWidth}
                    y1={y + currentRowHeight}
                    y2={y + currentRowHeight}
                    stroke={colors.background.grid}
                    strokeWidth={1}
                  />

                  {/* Midnight guidelines (behind bars, per row) - filter to visible range */}
                  {midnightDates
                    .filter((d) => {
                      const x = timeScale(d);
                      return x >= 0 && x <= innerWidth;
                    })
                    .map((d, i) => (
                      <line
                        key={`midnight-${i}`}
                        x1={timeScale(d)}
                        x2={timeScale(d)}
                        y1={y}
                        y2={y + Math.min(rowHeight, currentRowHeight)}
                        stroke="#DDDDDD"
                        strokeWidth={1}
                      />
                    ))}

                  {/* Stop label with wrapping */}
                  <foreignObject
                    x={-margin.left + 10}
                    y={y + 5}
                    width={margin.left - 20}
                    height={Math.min(rowHeight - 10, currentRowHeight - 10)}
                    style={{ zIndex: 10 }}>
                    <div className={styles.rowLabel}>
                      <div className={styles.rowLabelTitle} style={{ color: colors.text.primary }}>
                        {(platformOrderEnabled ? stop.platform_sequence_number : stop.display_sequence_number) ??
                          stop.display_sequence_number}
                        . {stop.location.name}
                      </div>
                      <div className={styles.rowLabelSubtitle} style={{ color: colors.text.secondary }}>
                        {stop.type}
                      </div>
                    </div>
                  </foreignObject>

                  {/* Timeslot bar - only render if stop has timeslot and it's within visible range */}
                  {stop.timeslot &&
                    (() => {
                      // Check if timeslot is within visible time domain
                      const [domainStart, domainEnd] = timeScale.domain();
                      const timeslotStart = new Date(stop.timeslot.begin);
                      const timeslotEnd = new Date(stop.timeslot.end);

                      // Skip rendering if timeslot is completely outside visible range
                      if (
                        timeslotEnd.getTime() < domainStart.getTime() ||
                        timeslotStart.getTime() > domainEnd.getTime()
                      ) {
                        return null;
                      }

                      // Clamp timeslot bar to visible chart area
                      const rawStartX = timeScale(timeslotStart);
                      const rawEndX = timeScale(timeslotEnd);
                      const startX = Math.max(0, rawStartX);
                      const endX = Math.min(innerWidth, rawEndX);
                      // Minimum width ensures zero-width timeslots are visible as a single line
                      const MIN_TIMESLOT_WIDTH = 1;
                      const barWidth = Math.max(MIN_TIMESLOT_WIDTH, endX - startX);

                      // Skip if timeslot is completely outside visible area
                      if (rawEndX < 0 || rawStartX > innerWidth) return null;

                      let fillColor: string;
                      let borderColor: string;
                      switch (stop.type) {
                        case 'loading':
                          fillColor = colors.timeslot.loading;
                          borderColor = interpolateBlues(0.8);
                          break;
                        case 'unloading':
                          fillColor = colors.timeslot.unloading;
                          borderColor = interpolateGreens(0.8);
                          break;
                        case 'waypoint':
                          fillColor = colors.timeslot.waypoint;
                          borderColor = interpolateOranges(0.8);
                          break;
                        case 'other':
                          fillColor = colors.timeslot.other;
                          borderColor = interpolateGreys(0.8);
                          break;
                        default:
                          fillColor = colors.timeslot.other;
                          borderColor = interpolateGreys(0.8);
                      }

                      const timeslotTooltip: TooltipData = {
                        title: stop.location.name,
                        subtitle: stop.location.address?.full_address,
                        rows: [
                          { label: 'Type', value: stop.type.charAt(0).toUpperCase() + stop.type.slice(1) },
                          { label: 'Status', value: stop.status },
                          {
                            label: 'Timeslot (UTC)',
                            value: `${formatUtcTime(new Date(stop.timeslot.begin))} - ${formatUtcTime(new Date(stop.timeslot.end))}`,
                            highlight: true,
                          },
                          ...(stop.arrival_time
                            ? [
                                {
                                  label: 'Actual Arrival (UTC)',
                                  value: formatUtcTimeSeconds(new Date(stop.arrival_time)),
                                  highlight: true,
                                },
                              ]
                            : []),
                          ...(stop.departure_time
                            ? [
                                {
                                  label: 'Actual Departure (UTC)',
                                  value: formatUtcTimeSeconds(new Date(stop.departure_time)),
                                  highlight: true,
                                },
                              ]
                            : []),
                        ],
                      };

                      return (
                        <g
                          onClick={() => toggleRowExpanded(`stop-${stop.stop_id}`)}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => showTooltip(e, timeslotTooltip)}
                          onMouseMove={updateTooltipPosition}
                          onMouseLeave={hideTooltip}>
                          {/* Main filled rectangle */}
                          <rect
                            x={startX}
                            y={barY}
                            width={barWidth}
                            height={barHeight}
                            fill={fillColor}
                            opacity={0.8}
                            stroke="none"
                          />

                          {/* Top border - same as main color */}
                          <line
                            x1={startX}
                            y1={barY}
                            x2={startX + barWidth}
                            y2={barY}
                            stroke={fillColor}
                            strokeWidth={1}
                            opacity={1}
                          />

                          {/* Bottom border - same as main color */}
                          <line
                            x1={startX}
                            y1={barY + barHeight}
                            x2={startX + barWidth}
                            y2={barY + barHeight}
                            stroke={fillColor}
                            strokeWidth={1}
                            opacity={1}
                          />

                          {/* Left border - darker, full height (only if not clipped) */}
                          {rawStartX >= 0 && (
                            <line
                              x1={startX}
                              y1={barY - 0.5}
                              x2={startX}
                              y2={barY + barHeight + 0.5}
                              stroke={borderColor}
                              strokeWidth={3}
                              opacity={1}
                            />
                          )}

                          {/* Right border - darker, full height (only if not clipped) */}
                          {rawEndX <= innerWidth && (
                            <line
                              x1={startX + barWidth}
                              y1={barY - 0.5}
                              x2={startX + barWidth}
                              y2={barY + barHeight + 0.5}
                              stroke={borderColor}
                              strokeWidth={3}
                              opacity={1}
                            />
                          )}
                        </g>
                      );
                    })()}

                  {/* All stop events */}
                  {(() => {
                    const unifiedStopEvents = buildUnifiedStopEvents(stop);

                    const eventsLineY = barY + barHeight + 12; // 12px margin below rectangle

                    // Helper to create tooltip data for stop events
                    const createStopEventTooltip = (event: UnifiedEvent): TooltipData => ({
                      title: event.label,
                      subtitle: event.isExternal ? 'External Event' : 'Stop Event',
                      rows: [
                        { label: 'Time (UTC)', value: formatUtcTimeSeconds(event.timestamp), highlight: true },
                        { label: 'Date (UTC)', value: formatUtcFullDate(event.timestamp) },
                        ...(event.createdAt
                          ? [{ label: 'Sent (UTC)', value: formatUtcDateTime(event.createdAt) }]
                          : []),
                        ...(event.isSentLate && event.sentDelayMinutes !== undefined
                          ? [
                              {
                                label: 'Warning',
                                value: `Sent ${formatDelayDuration(event.sentDelayMinutes)} late`,
                                warning: true,
                              },
                            ]
                          : []),
                        { label: 'Stop', value: stop.location.name },
                        ...(event.isGpsEvent !== undefined
                          ? [{ label: 'Type', value: event.isGpsEvent ? 'GPS' : 'Non-GPS' }]
                          : []),
                      ],
                    });

                    // Get the current time domain for filtering out-of-range events
                    const [domainStart, domainEnd] = timeScale.domain();

                    // Filter events that are within the visible time domain
                    const visibleStopEvents = unifiedStopEvents.filter((event) => {
                      const eventTime = event.timestamp.getTime();
                      return eventTime >= domainStart.getTime() && eventTime <= domainEnd.getTime();
                    });
                    const centerOfTimeslot = barY + barHeight / 2; // Center of the timeslot rectangle
                    const visibleStopEventPositions = visibleStopEvents.map((event, eventIndex) => {
                      const x = timeScale(event.timestamp);
                      const collapsedY = centerOfTimeslot;
                      const expandedY = eventsLineY + eventIndex * 16; // Stack events with 16px spacing
                      const eventY = isExpanded ? expandedY : collapsedY;

                      return {
                        event,
                        eventIndex,
                        x,
                        collapsedY,
                        eventY,
                      };
                    });
                    const actualArrivalPosition = visibleStopEventPositions.find(
                      ({ event }) => !event.isExternal && event.label === 'Arrival',
                    );
                    const actualDeparturePosition = visibleStopEventPositions.find(
                      ({ event }) => !event.isExternal && event.label === 'Departure',
                    );
                    const actualStopSegment =
                      actualArrivalPosition && actualDeparturePosition
                        ? (() => {
                            const hasSameTimestamp =
                              actualArrivalPosition.event.timestamp.getTime() ===
                              actualDeparturePosition.event.timestamp.getTime();
                            const dx = actualDeparturePosition.x - actualArrivalPosition.x;
                            const dy = actualDeparturePosition.eventY - actualArrivalPosition.eventY;
                            const distance = Math.hypot(dx, dy);

                            if (hasSameTimestamp) {
                              if (distance === 0) {
                                return {
                                  kind: 'double-marker' as const,
                                  x: actualArrivalPosition.x,
                                  y: actualArrivalPosition.eventY,
                                };
                              }

                              const offset = 2;
                              const normalX = -dy / distance;
                              const normalY = dx / distance;

                              return {
                                kind: 'double' as const,
                                lineA: {
                                  x1: actualArrivalPosition.x + normalX * offset,
                                  y1: actualArrivalPosition.eventY + normalY * offset,
                                  x2: actualDeparturePosition.x + normalX * offset,
                                  y2: actualDeparturePosition.eventY + normalY * offset,
                                },
                                lineB: {
                                  x1: actualArrivalPosition.x - normalX * offset,
                                  y1: actualArrivalPosition.eventY - normalY * offset,
                                  x2: actualDeparturePosition.x - normalX * offset,
                                  y2: actualDeparturePosition.eventY - normalY * offset,
                                },
                              };
                            }

                            const trim = Math.min(4.5, distance / 2);

                            return {
                              kind: 'single' as const,
                              x1: actualArrivalPosition.x + (dx / distance) * trim,
                              y1: actualArrivalPosition.eventY + (dy / distance) * trim,
                              x2: actualDeparturePosition.x - (dx / distance) * trim,
                              y2: actualDeparturePosition.eventY - (dy / distance) * trim,
                            };
                          })()
                        : null;

                    return (
                      <>
                        {platformOrderEnabled &&
                          actualStopSegment &&
                          (actualStopSegment.kind === 'single' ? (
                            <line
                              x1={actualStopSegment.x1}
                              y1={actualStopSegment.y1}
                              x2={actualStopSegment.x2}
                              y2={actualStopSegment.y2}
                              stroke="#9E9E9E"
                              strokeWidth={2}
                              strokeLinecap="round"
                              opacity={0.9}
                            />
                          ) : actualStopSegment.kind === 'double' ? (
                            <g opacity={0.9}>
                              <line
                                x1={actualStopSegment.lineA.x1}
                                y1={actualStopSegment.lineA.y1}
                                x2={actualStopSegment.lineA.x2}
                                y2={actualStopSegment.lineA.y2}
                                stroke="#9E9E9E"
                                strokeWidth={2}
                                strokeLinecap="round"
                              />
                              <line
                                x1={actualStopSegment.lineB.x1}
                                y1={actualStopSegment.lineB.y1}
                                x2={actualStopSegment.lineB.x2}
                                y2={actualStopSegment.lineB.y2}
                                stroke="#9E9E9E"
                                strokeWidth={2}
                                strokeLinecap="round"
                              />
                            </g>
                          ) : (
                            <g opacity={0.9}>
                              <line
                                x1={actualStopSegment.x - 5}
                                y1={actualStopSegment.y - 2}
                                x2={actualStopSegment.x + 5}
                                y2={actualStopSegment.y - 2}
                                stroke="#9E9E9E"
                                strokeWidth={2}
                                strokeLinecap="round"
                              />
                              <line
                                x1={actualStopSegment.x - 5}
                                y1={actualStopSegment.y + 2}
                                x2={actualStopSegment.x + 5}
                                y2={actualStopSegment.y + 2}
                                stroke="#9E9E9E"
                                strokeWidth={2}
                                strokeLinecap="round"
                              />
                            </g>
                          ))}
                        {/* Render events */}
                        {visibleStopEventPositions.map(({ event, x, collapsedY, eventY }) => {
                          const stopEventTooltip = createStopEventTooltip(event);

                          return (
                            <g
                              key={event.eventId}
                              onClick={() => toggleRowExpanded(`stop-${stop.stop_id}`)}
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={(e) => showTooltip(e, stopEventTooltip)}
                              onMouseMove={updateTooltipPosition}
                              onMouseLeave={hideTooltip}>
                              <g
                                style={{
                                  transition: 'transform 0.3s ease',
                                  transform: `translateY(${eventY - collapsedY}px)`,
                                }}>
                                {event.isExternal ? (
                                  <rect
                                    x={x - 3.5}
                                    y={collapsedY - 3.5}
                                    width={7}
                                    height={7}
                                    fill={event.isArrival ? '#4CAF50' : event.isDeparture ? '#F44336' : '#BDBDBD'}
                                    stroke={event.isArrival ? '#2E7D32' : event.isDeparture ? '#C62828' : '#9E9E9E'}
                                    strokeWidth={1.5}
                                    opacity={0.9}
                                  />
                                ) : (
                                  <circle
                                    cx={x}
                                    cy={collapsedY}
                                    r={3.5}
                                    style={{
                                      fill: event.isArrival ? '#4CAF50' : event.isDeparture ? '#F44336' : '#BDBDBD',
                                      stroke: event.isArrival ? '#2E7D32' : event.isDeparture ? '#C62828' : '#9E9E9E',
                                      strokeWidth: 1.5,
                                      opacity: 0.9,
                                    }}
                                  />
                                )}
                                <text
                                  x={x + 6}
                                  y={collapsedY}
                                  fontSize="10px"
                                  fill={event.isSentLate ? '#C62828' : colors.text.primary}
                                  dominantBaseline="middle"
                                  style={{
                                    opacity: isExpanded ? 1 : 0,
                                    transition: isExpanded ? 'opacity 0.15s ease 0.2s' : 'opacity 0s',
                                  }}>
                                  {event.label} - {formatUtcTimeSeconds(event.timestamp)} UTC
                                </text>
                              </g>
                            </g>
                          );
                        })}
                      </>
                    );
                  })()}
                </g>
              );
            })}

            {platformOrderEnabled && (
              <g pointerEvents="none">
                {sameTimestampTypeSegments.map((segment) => (
                  <g key={segment.key}>
                    <line
                      x1={segment.lineA.x1}
                      y1={segment.lineA.y1}
                      x2={segment.lineA.x2}
                      y2={segment.lineA.y2}
                      stroke="#CFCFCF"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      opacity={0.8}
                    />
                    <line
                      x1={segment.lineB.x1}
                      y1={segment.lineB.y1}
                      x2={segment.lineB.x2}
                      y2={segment.lineB.y2}
                      stroke="#CFCFCF"
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      opacity={0.8}
                    />
                  </g>
                ))}
                {stopConnectionSegments.map((segment) => {
                  const angle = (Math.atan2(segment.y2 - segment.y1, segment.x2 - segment.x1) * 180) / Math.PI;
                  const midX = (segment.x1 + segment.x2) / 2;
                  const midY = (segment.y1 + segment.y2) / 2;
                  const labelX = midX + 7;
                  const labelY = midY;
                  const distanceLabel =
                    segment.distanceKm == null
                      ? null
                      : `${segment.distanceKm > 10 ? Math.round(segment.distanceKm) : Number(segment.distanceKm.toFixed(1))} km`;

                  return (
                    <g key={segment.key}>
                      <line
                        x1={segment.x1}
                        y1={segment.y1}
                        x2={segment.x2}
                        y2={segment.y2}
                        stroke="#FBC02D"
                        strokeWidth={2.5}
                        strokeLinecap="round"
                        opacity={0.9}
                      />
                      <polygon
                        points="-5,-4 5,0 -5,4"
                        fill="#FBC02D"
                        opacity={0.95}
                        transform={`translate(${midX}, ${midY}) rotate(${angle})`}
                      />
                      {distanceLabel && (
                        <text
                          x={labelX}
                          y={labelY}
                          textAnchor="start"
                          dominantBaseline="middle"
                          fill={colors.text.primary}
                          style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            paintOrder: 'stroke',
                            stroke: colors.text.labelStroke,
                            strokeWidth: 3,
                            strokeLinecap: 'round',
                            strokeLinejoin: 'round',
                          }}>
                          {distanceLabel}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            )}

            {/* Time axis for stops - clipped to visible area */}
            <g clipPath="url(#stops-clip)">
              <g ref={axisRef} transform={`translate(0,${stopsHeight})`} />
            </g>

            {/* Midnight date labels under stops axis - filter to visible range */}
            <g>
              {midnightDates
                .filter((d) => {
                  const x = timeScale(d);
                  return x >= 0 && x <= innerWidth;
                })
                .map((d, i) => (
                  <text
                    key={`midnight-date-stops-${i}`}
                    x={timeScale(d)}
                    y={stopsHeight + 30}
                    textAnchor="middle"
                    fill={colors.text.secondary}
                    style={{ fontSize: '11px' }}>
                    {formatUtcIsoDate(d)}
                  </text>
                ))}
            </g>

            {/* Vehicle Timeline Panel */}
            {data.vehicles.length > 0 && (
              <g transform={`translate(0,${stopsHeight + panelSpacing})`}>
                {/* Panel header line */}
                <line
                  x1={-margin.left}
                  x2={innerWidth + margin.right}
                  y1={-panelSpacing / 2}
                  y2={-panelSpacing / 2}
                  stroke={colors.background.grid}
                  strokeWidth={2}
                />

                {/* Vehicle rows */}
                {data.vehicles.map((vehicle, vehicleIndex) => {
                  // Calculate cumulative Y position for this vehicle (including spacing)
                  let vehicleY = 0;
                  for (let i = 0; i < vehicleIndex; i++) {
                    vehicleY += rowHeights[`vehicle-${data.vehicles[i].vehicleId}`] + vehicleRowSpacing;
                  }

                  const rowId = `vehicle-${vehicle.vehicleId}`;
                  const currentRowHeight = rowHeights[rowId];
                  const isExpanded = expandedRows.has(rowId);

                  return (
                    <g key={vehicle.vehicleId}>
                      {/* Row background */}
                      <rect
                        x={0}
                        y={vehicleY}
                        width={innerWidth}
                        height={currentRowHeight}
                        fill={
                          vehicleIndex % 2 === 0 ? vehicleColors.vehicleBackground : vehicleColors.vehicleBackgroundAlt
                        }
                        onClick={() => toggleRowExpanded(rowId)}
                        style={{ cursor: 'pointer' }}
                      />

                      {/* Tracking range overlay (gray out non-tracking periods) - with animation */}
                      {(() => {
                        // Calculate the expanded tracking line position
                        const uniqueStopIds = new Set(vehicle.nearStopRanges.map((r) => r.stopId));
                        const uniqueStopCount = Math.max(1, uniqueStopIds.size);
                        const lineHeight = 18;
                        const expandedStartY = vehicleY + 5;
                        // Line indices: allocation (0), near stops (1..N), breaks (N+1), location (N+2), tracking (N+3)
                        // trackingLineIndex = uniqueStopCount + 3 (after near stops, breaks, location)
                        const trackingLineIndex = uniqueStopCount + 3;
                        const expandedTrackingY = expandedStartY + trackingLineIndex * lineHeight + 1;
                        const expandedTrackingHeight = 14;

                        // Collapsed position calculations (must match renderVehicleTimelineWithTransition)
                        const collapsedRowHeight = 8;
                        const collapsedPadding = 6;
                        const collapsedRowSpacing = 3;
                        const collapsedTrackingY =
                          vehicleY + collapsedPadding + 3 * (collapsedRowHeight + collapsedRowSpacing);

                        return renderTrackingOverlay(
                          vehicle,
                          collapsedTrackingY,
                          collapsedRowHeight,
                          timeScale,
                          innerWidth,
                          isExpanded,
                          expandedTrackingY,
                          expandedTrackingHeight,
                          showTooltip,
                          updateTooltipPosition,
                          hideTooltip,
                          vehicleColors,
                        );
                      })()}

                      {/* Row separator */}
                      <line
                        x1={-margin.left}
                        x2={innerWidth}
                        y1={vehicleY + currentRowHeight}
                        y2={vehicleY + currentRowHeight}
                        stroke={colors.background.grid}
                        strokeWidth={1}
                      />

                      {/* Midnight guidelines - filter to visible range */}
                      {midnightDates
                        .filter((d) => {
                          const x = timeScale(d);
                          return x >= 0 && x <= innerWidth;
                        })
                        .map((d, i) => (
                          <line
                            key={`vehicle-midnight-${i}`}
                            x1={timeScale(d)}
                            x2={timeScale(d)}
                            y1={vehicleY}
                            y2={vehicleY + currentRowHeight}
                            stroke="#DDDDDD"
                            strokeWidth={1}
                          />
                        ))}

                      {/* Vehicle label (license plate) */}
                      <foreignObject
                        x={-margin.left + 10}
                        y={vehicleY + 5}
                        width={margin.left - 20}
                        height={Math.min(40, currentRowHeight - 10)}>
                        <div className={styles.vehicleLabel}>
                          <div className={styles.vehicleLabelTitle} style={{ color: colors.text.primary }}>
                            🚛 {vehicle.licensePlateNumber}
                          </div>
                        </div>
                      </foreignObject>

                      {/* Vehicle timeline content with transitions */}
                      {renderVehicleTimelineWithTransition(
                        vehicle,
                        vehicleY,
                        48, // collapsed height (matches getVehicleRowHeight)
                        timeScale,
                        innerWidth,
                        rowId,
                        isExpanded,
                        toggleRowExpanded,
                        showTooltip,
                        updateTooltipPosition,
                        hideTooltip,
                        colors,
                        vehicleColors,
                      )}
                    </g>
                  );
                })}

                {/* Vehicle time axis - using UTC-aligned ticks, filtered to visible range */}
                <g transform={`translate(0,${vehiclesHeight})`} clipPath="url(#vehicle-clip)">
                  {axisTicks
                    .filter((tick) => {
                      const x = timeScale(tick);
                      return x >= 0 && x <= innerWidth;
                    })
                    .map((tick, i) => (
                      <g key={i} transform={`translate(${timeScale(tick)},0)`}>
                        <line y2={6} stroke={colors.background.grid} />
                        <text y={18} textAnchor="middle" fill={colors.text.secondary} fontSize="10px">
                          {formatUtcTime(tick)}
                        </text>
                      </g>
                    ))}
                  <line x1={0} x2={innerWidth} stroke={colors.background.grid} />
                </g>
              </g>
            )}

            {/* Distance labels next to rows (5px gap from timeline content) */}
            {!platformOrderEnabled && (
              <g>
                {consecutiveDistancesKm.map((dist, i) => {
                  if (dist == null) return null;

                  // Calculate cumulative Y position for the distance label (between two stops)
                  let labelY = rowHeights['tour-events'];
                  for (let j = 0; j <= i; j++) {
                    labelY += rowHeights[`stop-${orderedStops[j].stop_id}`];
                  }

                  return (
                    <text
                      key={`dist-row-${i}`}
                      x={innerWidth + 5}
                      y={labelY}
                      textAnchor="start"
                      dominantBaseline="middle"
                      fill={colors.text.primary}
                      style={{ fontSize: '12px', fontWeight: 600 }}>
                      {`${dist > 10 ? Math.round(dist) : Number(dist.toFixed(1))} km`}
                    </text>
                  );
                })}
              </g>
            )}
          </g>
        </svg>
      </div>

      {/* Tooltip */}
      <TimelineTooltip tooltip={tooltip} />
    </div>
  );
};
